import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import { CustomerPreview } from '@/hooks/useCustomersPreviewFilter'
import { cn } from '@/lib/utils'
import {
    SURVEY_CATEGORY_LABELS,
    UPSALE_SERVICE_MAP,
    UpsalePotentialValue,
} from '@/modules/Survey/constants'
import { RecordResponse } from '@/modules/Survey/types'
import { getServiceColor } from '@/constants/serviceColors'
import PrivacyField from '@/components/PrivacyField'
import { User, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { generatePathDatacentral, paths } from '@/utils/path'

const MAX_VISIBLE_SERVICES = 3

interface SurveyResponseCardProps {
    customer: CustomerPreview
    responded: boolean
    response?: RecordResponse
    onRemove?: (customerId: number) => void
    onClick: (customer: CustomerPreview) => void
    'data-tour'?: string
}

const SurveyResponseCard = ({
    customer,
    responded,
    response,
    onRemove,
    onClick,
    'data-tour': dataTour,
}: SurveyResponseCardProps) => {
    const { t } = useTranslation('surveys')
    const [servicesExpanded, setServicesExpanded] = useState(false)

    const categories = [
        {
            key: 'score_service_quality' as const,
            label: t(SURVEY_CATEGORY_LABELS.service_quality),
            value: response?.score_service_quality || 0,
        },
        {
            key: 'score_customer_care' as const,
            label: t(SURVEY_CATEGORY_LABELS.customer_care),
            value: response?.score_customer_care || 0,
        },
        {
            key: 'score_tech_support' as const,
            label: t(SURVEY_CATEGORY_LABELS.tech_support),
            value: response?.score_tech_support || 0,
        },
        {
            key: 'score_issue_resolution' as const,
            label: t(SURVEY_CATEGORY_LABELS.issue_resolution),
            value: response?.score_issue_resolution || 0,
        },
    ]

    const averageScore = (
        categories.reduce((acc, curr) => acc + curr.value, 0) /
        categories.length
    ).toFixed(1)

    const upsaleValues = response?.upsale_potential || []

    const cardContent = (
        <Card
            data-tour={dataTour}
            className="transition-all duration-200 hover:shadow-md cursor-pointer group bg-white border-gray-200 hover:border-blue-300 rounded-xl shadow-sm w-full relative overflow-hidden"
            onClick={() => onClick(customer)}
        >
            <CardContent className="p-5 flex flex-col h-full">
                {/* Header: Icon + Name & Remove Action */}
                <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2 min-w-0">
                        <div className="size-5 rounded flex items-center justify-center shrink-0 bg-blue-50 text-blue-600">
                            <User className="size-3" />
                        </div>
                        <div className="min-w-0">
                            <span className="block text-blue-600 font-medium text-sm truncate">
                                {customer.customer_name}
                            </span>
                            {/* stopPropagation: the whole card opens the
                                response dialog on click. */}
                            <Link
                                to={generatePathDatacentral(
                                    paths.customerDetail,
                                    { id: customer.customer_id }
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="block text-xs text-gray-500 hover:text-primary hover:underline truncate uppercase"
                            >
                                {customer.customer_id} -{' '}
                                {customer.company_name ||
                                    customer.customer_name}
                            </Link>
                        </div>
                    </div>

                    {onRemove && (
                        <Button
                            onClick={(e) => {
                                e.stopPropagation()
                                onRemove(customer.customer_id)
                            }}
                            variant="ghost"
                            size="icon"
                            className="size-6 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full min-w-0 shrink-0 absolute top-4 right-4"
                        >
                            <X className="size-3.5" />
                        </Button>
                    )}
                </div>

                {/* Main Content: Phone & Service Group Badges */}
                <div className="mt-3 pr-6 flex-1">
                    <div className="text-sm text-gray-900 min-h-[21px]">
                        {customer.customer_phone ? (
                            <div onClick={(e) => e.stopPropagation()}>
                                <PrivacyField
                                    value={customer.customer_phone}
                                    type="phone"
                                    toggleable
                                />
                            </div>
                        ) : (
                            '—'
                        )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1 min-h-[64px] content-start">
                        {customer.service_groups?.length ? (
                            <>
                                {(servicesExpanded
                                    ? customer.service_groups
                                    : customer.service_groups.slice(
                                          0,
                                          MAX_VISIBLE_SERVICES
                                      )
                                ).map((sg) => (
                                    <span
                                        key={sg}
                                        className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700"
                                    >
                                        {sg}
                                    </span>
                                ))}
                                {customer.service_groups.length >
                                    MAX_VISIBLE_SERVICES && (
                                    <span
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setServicesExpanded((v) => !v)
                                        }}
                                        className="text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-500 cursor-pointer hover:bg-gray-300 transition-colors"
                                    >
                                        {servicesExpanded
                                            ? '−'
                                            : `+${customer.service_groups.length - MAX_VISIBLE_SERVICES}`}
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className="text-xs text-gray-500">—</span>
                        )}
                    </div>
                </div>

                {/* Footer: Score/Status Badges & Action Link */}
                <div className="flex flex-col gap-2 mt-auto pt-4 border-t border-gray-100">
                    <div className="flex flex-col gap-2">
                        {responded ? (
                            <>
                                {upsaleValues.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                        {upsaleValues.map((v) => {
                                            const sc = getServiceColor(
                                                UPSALE_SERVICE_MAP[
                                                    v as UpsalePotentialValue
                                                ]
                                            )
                                            return (
                                                <span
                                                    key={v}
                                                    className="text-xs px-3 py-1 font-medium rounded"
                                                    style={{
                                                        backgroundColor: sc.bg,
                                                        color: sc.text,
                                                    }}
                                                >
                                                    {v}
                                                </span>
                                            )
                                        })}
                                    </div>
                                )}
                                <div>
                                    <span className="text-xs px-3 py-1 font-medium rounded bg-blue-50 text-blue-500">
                                        {t('surveys.average_score', 'Avg')}:{' '}
                                        {averageScore}
                                    </span>
                                </div>
                            </>
                        ) : (
                            <span className="text-xs px-3 py-1 font-medium rounded bg-gray-100 text-gray-700">
                                {t('surveys.status.pending', 'Pending')}
                            </span>
                        )}
                    </div>

                    <Button
                        variant={responded ? 'outline' : 'default'}
                        size="sm"
                        className="h-7 text-xs px-3 shrink-0"
                    >
                        {responded
                            ? t('surveys.responses.edit', 'Chỉnh sửa')
                            : t(
                                  'surveys.responses.add_response',
                                  'Thêm phản hồi'
                              )}
                    </Button>
                </div>
            </CardContent>
        </Card>
    )

    if (!response || !responded) {
        return cardContent
    }

    return (
        <TooltipProvider delayDuration={300}>
            <Tooltip>
                <TooltipTrigger asChild>{cardContent}</TooltipTrigger>
                <TooltipContent
                    className="w-[280px] sm:w-[320px] p-0 overflow-hidden shadow-xl"
                    sideOffset={8}
                >
                    {/* Tooltip Header */}
                    <div className="bg-muted/40 p-2.5 sm:p-3 border-b flex items-center justify-between">
                        <span className="font-semibold text-xs sm:text-sm">
                            {t('surveys.tooltip.title', 'Response Details')}
                        </span>
                        <div className="flex items-center gap-1.5 bg-background border px-2 py-0.5 rounded-sm shadow-sm">
                            <span className="text-[10px] uppercase font-bold text-muted-foreground">
                                {t('surveys.average_score', 'Avg')}:
                            </span>
                            <span className="text-sm font-bold text-primary">
                                {averageScore}
                            </span>
                        </div>
                    </div>

                    {/* Tooltip Body */}
                    <div className="p-2.5 sm:p-3 space-y-3 sm:space-y-4">
                        <div className="space-y-2">
                            {categories.map((category) => (
                                <div
                                    key={category.key}
                                    className="flex items-center justify-between text-xs"
                                >
                                    <span className="text-muted-foreground mr-3 sm:mr-4 truncate">
                                        {category.label}
                                    </span>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <div className="h-1.5 w-12 sm:w-16 bg-muted rounded-full overflow-hidden">
                                            <div
                                                className={cn(
                                                    'h-full rounded-full',
                                                    category.value >= 8
                                                        ? 'bg-green-500'
                                                        : category.value >= 5
                                                          ? 'bg-yellow-500'
                                                          : 'bg-red-500'
                                                )}
                                                style={{
                                                    width: `${(category.value / 10) * 100}%`,
                                                }}
                                            />
                                        </div>
                                        <span className="font-medium w-4 text-right">
                                            {category.value}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {response.score_referral_willingness != null && (
                            <div className="pt-3 border-t">
                                <span className="text-xs font-semibold text-muted-foreground mb-1 block">
                                    {t(
                                        'surveys.column.score_referral_willingness',
                                        'Referral Willingness'
                                    )}
                                    :
                                </span>
                                <p className="text-xs font-medium text-foreground/90 bg-muted/30 p-2 rounded border">
                                    {response.score_referral_willingness}
                                </p>
                            </div>
                        )}

                        {/* Upsale Potential in tooltip */}
                        {upsaleValues.length > 0 && (
                            <div className="pt-3 border-t">
                                <span className="text-xs font-semibold text-muted-foreground mb-1 block">
                                    {t(
                                        'surveys.column.upsale_potential',
                                        'Upsale Potential'
                                    )}
                                    :
                                </span>
                                <div className="flex flex-wrap gap-1">
                                    {upsaleValues.map((v) => {
                                        const sc = getServiceColor(
                                            UPSALE_SERVICE_MAP[
                                                v as UpsalePotentialValue
                                            ]
                                        )
                                        return (
                                            <span
                                                key={v}
                                                className="px-2 py-0.5 text-xs font-medium rounded"
                                                style={{
                                                    backgroundColor: sc.bg,
                                                    color: sc.text,
                                                }}
                                            >
                                                {v}
                                            </span>
                                        )
                                    })}
                                </div>
                            </div>
                        )}

                        {response.note_general && (
                            <div className="pt-3 border-t">
                                <span className="text-xs font-semibold text-muted-foreground mb-1 block">
                                    {t(
                                        'surveys.form.note_general',
                                        'General Note'
                                    )}
                                    :
                                </span>
                                <p className="text-xs italic text-foreground/90 bg-muted/30 p-2 rounded border leading-relaxed">
                                    "{response.note_general}"
                                </p>
                            </div>
                        )}
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}

export default SurveyResponseCard
